// pages/Timesheet.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Paper, Typography, Button, TextField, Autocomplete,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, Tooltip,
  IconButton, Alert, Link, Snackbar
} from "@mui/material";
import SaveOutlined from "@mui/icons-material/SaveOutlined";
import AddOutlined from "@mui/icons-material/AddOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import FilterAltOff from "@mui/icons-material/FilterAltOff";

import {
  collection, query, where, getDocs, doc, writeBatch,
  orderBy, startAt, endAt
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useAuth } from "../authContext";

/* --------------------------- helpers --------------------------- */
const pad2 = (n) => String(n).padStart(2, "0");
const toKey = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const fromKey = (s) => {
  const [y, m, d] = s.split("-").map((x) => parseInt(x, 10));
  return new Date(y, m - 1, d);
};
const enumerateKeys = (startKey, endKey) => {
  const out = [];
  const s = fromKey(startKey), e = fromKey(endKey);
  s.setHours(0, 0, 0, 0); e.setHours(0, 0, 0, 0);
  const d = new Date(s);
  while (d <= e) { out.push(toKey(d)); d.setDate(d.getDate() + 1); }
  return out;
};
const startOfWeek = (d) => {
  const x = new Date(d);
  const day = x.getDay();
  x.setDate(x.getDate() - ((day + 6) % 7)); // Monday start
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOfWeek = (d) => {
  const s = startOfWeek(d);
  s.setDate(s.getDate() + 6);
  return s;
};
const DOW = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const rowKey = (pId, area) => `${pId}__${(area || "").trim().toLowerCase()}`;


// Safe slug for Firestore document IDs (no "/" and other risky chars)
const safeIdPart = (s) =>
  (s ?? "")
    .toString()
    .normalize("NFKC")
    .replace(/[\/#[\]?]/g, "-")  // ban "/", "#", "[", "]", "?"
    .replace(/\s+/g, "_")        // collapse whitespace
    .slice(0, 150);              // keep IDs reasonable


// ---- time helpers for hh:mm entry ----
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const hmToMinutes = (val) => {
  if (val === null || val === undefined) return null;
  const s = String(val).trim();
  if (!s) return null;

  if (/^\d+:\d{1,2}$/.test(s)) {
    const [h, m] = s.split(":").map((x) => parseInt(x, 10) || 0);
    return clamp(h * 60 + clamp(m, 0, 59), 0, 24 * 60);
  }
  const n = parseFloat(s);
  if (Number.isFinite(n)) return clamp(Math.round(n * 60), 0, 24 * 60);
  return null;
};
const minutesToHM = (m) => {
  if (m === null || m === undefined) return "";
  const mm = Math.max(0, Math.round(m));
  const h = Math.floor(mm / 60);
  const r = mm % 60;
  return `${h}:${pad2(r)}`;
};

export default function Timesheet() {
  const { companyId, user } = useAuth();
  const role = user?.role || "Member";
  const isAdminOrManager = role === "Admin" || role === "Manager";

  // toast/snackbar state
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  /* ======== filters (user + week) ======== */
  const [users, setUsers] = useState([]);               // {uid,name}
  const [selectedUser, setSelectedUser] = useState(null);

  const today = useMemo(() => new Date(), []);
  const [startKey, setStartKey] = useState(toKey(startOfWeek(today)));
  const [endKey, setEndKey] = useState(toKey(endOfWeek(today)));
  const dayKeys = useMemo(() => enumerateKeys(startKey, endKey), [startKey, endKey]);

  /* ======== project/area pickers ======== */
  const [projects, setProjects] = useState([]);         // {id,name,entity}
  const [projectOptions, setProjectOptions] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

 // NEW: Entity filter
  const [entityOptions, setEntityOptions] = useState([]); // ["KHR_ARC_CO_Ltd", ...]
  const [selectedEntity, setSelectedEntity] = useState(null);


  const [areaOptions, setAreaOptions] = useState([]);   // ["Audit", "Consulting", ...]
  const [selectedArea, setSelectedArea] = useState(null);


  

  /* ======== grid data ======== */
  const [rows, setRows] = useState([]);                 // [{id,pId,pName,entity,area}]
  const [cells, setCells] = useState({});               // {"{rowId}:{dateKey}": "hh:mm"}
  const [loading, setLoading] = useState(false);

  /* -------- load users -------- */
  useEffect(() => {
    if (!companyId || !user) return;
    (async () => {
      if (isAdminOrManager) {
        const snap = await getDocs(
          query(collection(db, "users"), where("companyId", "==", companyId))
        );
        const list = snap.docs.map(d => {
          const v = d.data() || {};
          return { uid: d.id, name: v.name || v.displayName || v.email || d.id };
        }).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setUsers(list);
        setSelectedUser(list[0] || null);
      } else {
        const me = { uid: user.uid, name: user.name || user.email || user.uid };
        setUsers([me]);
        setSelectedUser(me);
      }
    })();
  }, [companyId, user, isAdminOrManager]);

  /* -------- load all projects for this company -------- */
   /* -------- load all projects for this company -------- */
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      const snap = await getDocs(
        query(collection(db, "projects"), where("companyId", "==", companyId))
      );
      const list = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setProjects(list);
      setProjectOptions(list); // default; will be scoped by user below

      // NEW: build entity dropdown from project.entity
      const ents = Array.from(
        new Set(
          list
            .map(p => (p.entity || "").toString().trim())
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b));
      setEntityOptions(ents);
    })();
  }, [companyId]);


  /* -------- scope project options by selected user (via tasks) -------- */
  useEffect(() => {
    if (!companyId || !selectedUser) {
      setProjectOptions(projects);
      return;
    }
    (async () => {
      try {
        const ts = await getDocs(
          query(collection(db, "tasks"), where("companyId", "==", companyId))
        );
        const me = (selectedUser.name || "").trim().toLowerCase();
        const allow = new Set(
          ts.docs
            .map(d => d.data() || {})
            .filter(t => (t.assignedTo || "").trim().toLowerCase() === me)
            .map(t => (t.project || "").trim())
            .filter(Boolean)
        );

        // also keep any projects already added as rows
        rows.forEach(r => allow.add((r.pName || "").trim()));

                const filteredByUser =
          allow.size === 0 ? projects : projects.filter(p => allow.has((p.name || "").trim()));

        const ent = (selectedEntity || "").toString().trim().toLowerCase();
        const filtered =
          !ent
            ? filteredByUser
            : filteredByUser.filter(p => ((p.entity || "").toString().trim().toLowerCase() === ent));

        filtered.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setProjectOptions(filtered);


        if (selectedProject && !filtered.some(p => p.id === selectedProject.id)) {
          setSelectedProject(null);
          setSelectedArea(null);
        }
      } catch (e) {
        console.error("scoping projects failed:", e);
        setProjectOptions(projects);
      }
    })();
  }, [companyId, selectedUser, projects, rows, selectedProject, selectedEntity]);

  /* -------- load area/process options for the selected project -------- */
  useEffect(() => {
    (async () => {
      setAreaOptions([]);
      setSelectedArea(null);
      if (!companyId || !selectedProject) return;

      const projName = (selectedProject?.name || "").trim();
      if (!projName) return;

      const snap = await getDocs(
        query(
          collection(db, "tasks"),
          where("companyId", "==", companyId),
          where("project", "==", projName)
        )
      );

      const uniq = new Set(
        snap.docs
          .map(d => {
            const t = d.data() || {};
            return (t.areaProcess ?? t.area ?? t.process ?? "").toString().trim();
          })
          .filter(Boolean)
      );
      setAreaOptions([...uniq].sort((a, b) => a.localeCompare(b)));
    })();
  }, [companyId, selectedProject]);

  /* -------- load current grid values for selected user + date range -------- */
  const loadGrid = async () => {
    if (!companyId || !selectedUser || !startKey || !endKey) return;
    setLoading(true);
    try {
      const qRef = query(
        collection(db, "timesheets"),
        where("companyId", "==", companyId),
        where("userId", "==", selectedUser.uid),
        orderBy("dateKey"),
        startAt(startKey),
        endAt(endKey)
      );
      const snap = await getDocs(qRef);

      const rowSet = new Map();
      const nextCells = {};
      snap.forEach(d => {
        const v = d.data() || {};
        const id = rowKey(v.projectId, v.area);
        rowSet.set(id, {
          id,
          pId: v.projectId,
          pName: v.projectName,
          entity: v.entity || "",
          area: v.area || ""
        });

        const mins = (v.minutes !== undefined && v.minutes !== null)
          ? parseInt(v.minutes, 10)
          : (v.hours !== undefined && v.hours !== null)
            ? Math.round(parseFloat(v.hours) * 60)
            : null;
        nextCells[`${id}:${v.dateKey}`] = minutesToHM(mins);
      });

      const arr = Array.from(rowSet.values()).sort((a, b) =>
        (a.pName || "").localeCompare(b.pName || "") ||
        (a.area || "").localeCompare(b.area || "")
      );

      setRows(arr);
      setCells(nextCells);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadGrid(); /* eslint-disable-next-line */ }, [companyId, selectedUser, startKey, endKey]);

  /* -------- add a new blank row -------- */
  const onAddRow = () => {
    if (!selectedProject) { alert("Please pick a project."); return; }
    if (!selectedArea) { alert("Please pick an area/process."); return; }

    const p = selectedProject;
    const id = rowKey(p.id, selectedArea);
    if (rows.some(r => r.id === id)) return;

    setRows(prev => [...prev, {
      id,
      pId: p.id,
      pName: p.name || "",
      entity: p.entity || "",
      area: selectedArea
    }]);
    setSelectedArea(null);
  };

  /* -------- edit one cell -------- */
  const onChangeCell = (id, dk, val) =>
    setCells(prev => ({ ...prev, [`${id}:${dk}`]: val }));

  /* -------- totals in minutes -------- */
  const rowTotalMins  = (id) =>
    dayKeys.reduce((s, dk) => s + (hmToMinutes(cells[`${id}:${dk}`]) || 0), 0);
  const dayTotalMins  = (dk) =>
    rows.reduce((s, r) => s + (hmToMinutes(cells[`${r.id}:${dk}`]) || 0), 0);
  const grandTotalMins = () =>
    rows.reduce((s, r) => s + rowTotalMins(r.id), 0);

  /* -------- clear filters -------- */
  const clearFilters = () => {
    const s = toKey(startOfWeek(today));
    const e = toKey(endOfWeek(today));
    setStartKey(s);
    setEndKey(e);
    setSelectedEntity(null);
    setSelectedProject(null);
    setSelectedArea(null);

    if (users.length && isAdminOrManager) setSelectedUser(users[0]);
  };

  /* -------- save (upsert + prune) -------- */
  const onSave = async () => {
    if (!companyId || !selectedUser) return;

    setLoading(true);
    try {
      // collect desired docs (minutes > 0)
      const keep = new Map();
      rows.forEach(r => {
        dayKeys.forEach(dk => {
          const raw = (cells[`${r.id}:${dk}`] ?? "").trim();
          if (!raw) return;
          const mins = hmToMinutes(raw);
          if (mins && mins > 0) {
            keep.set(`${r.id}:${dk}`, {
              companyId,
              userId: selectedUser.uid,
              userName: selectedUser.name || "",
              dateKey: dk,
              projectId: r.pId,
              projectName: r.pName,
              entity: r.entity || "",
              area: r.area || "",
              minutes: mins,
              hours: +(mins / 60).toFixed(2),
              updatedAt: new Date().toISOString()
            });
          }
        });
      });

      const batch = writeBatch(db);

      // delete anything removed/zeroed in the selected range
      const qRef = query(
        collection(db, "timesheets"),
        where("companyId", "==", companyId),
        where("userId", "==", selectedUser.uid),
        orderBy("dateKey"), startAt(startKey), endAt(endKey)
      );
      const snap = await getDocs(qRef);
      snap.forEach(d => {
        const v = d.data() || {};
        const id = rowKey(v.projectId, v.area);
        const key = `${id}:${v.dateKey}`;
        if (!keep.has(key)) batch.delete(doc(db, "timesheets", d.id));
      });

      // upsert desired docs with a stable composite id
      for (const [key, v] of keep.entries()) {
        const [, dk] = key.split(":");
        const tsId = [
            safeIdPart(companyId),
            safeIdPart(selectedUser.uid),
            safeIdPart(dk),
            safeIdPart(v.projectId),
            safeIdPart(v.area)
          ].filter(Boolean).join("__");

          batch.set(doc(db, "timesheets", tsId), v, { merge: true });
      }

      await batch.commit();
      await loadGrid(); // refresh the grid with saved data

      setSnack({ open: true, message: "Saved successfully", severity: "success" });
    } catch (err) {
      console.error(err);
      setSnack({ open: true, message: err.message || "Save failed", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const onDeleteRow = (id) => {
    setRows(prev => prev.filter(r => r.id !== id));
    const next = { ...cells };
    dayKeys.forEach(dk => delete next[`${id}:${dk}`]);
    setCells(next);
  };

  /* --------------------------- UI --------------------------- */
  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>Timesheet</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <Autocomplete
            size="small"
            sx={{ width: 300 }}
            options={users}
            value={selectedUser}
            onChange={(_, v) => setSelectedUser(v)}
            getOptionLabel={(o) => o?.name ?? ""}
            isOptionEqualToValue={(a, b) => a?.uid === b?.uid}
            renderInput={(p) => <TextField {...p} label="User" placeholder={isAdminOrManager ? "Choose user" : "You"} />}
            disabled={!isAdminOrManager}
          />

          <TextField
            size="small"
            label="Week start"
            type="date"
            sx={{ width: 160 }}
            value={startKey}
            onChange={(e) => setStartKey(e.target.value)}
          />
          <TextField
            size="small"
            label="Week end"
            type="date"
            sx={{ width: 160 }}
            value={endKey}
            onChange={(e) => setEndKey(e.target.value)}
          />

          <Button startIcon={<FilterAltOff />} onClick={clearFilters}>Clear</Button>

          <Box sx={{ flex: 1 }} />
          <Button variant="contained" startIcon={<SaveOutlined />} onClick={onSave} disabled={loading}>
            Save
          </Button>
        </Box>

        {/* Add Row */}
        {/* Add Row */}
<Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: "wrap" }}>

  {/* NEW: Entity filter */}
  <Autocomplete
    size="small"
    options={entityOptions}
    value={selectedEntity}
    onChange={(_, v) => {
      setSelectedEntity(v || null);
      setSelectedProject(null);
      setSelectedArea(null);
    }}
    renderInput={(params) => (
      <TextField {...params} label="Entity" placeholder="All entities" />
    )}
    sx={{ minWidth: 280 }}
  />

  <Autocomplete
    size="small"
    options={projectOptions}
    value={selectedProject}
    onChange={(_, v) => { setSelectedProject(v || null); setSelectedArea(null); }}
    getOptionLabel={(o) => o?.name ?? ""}
    isOptionEqualToValue={(a, b) => a?.id === b?.id}
    renderInput={(params) => (
      <TextField {...params} label="Project (for new row)" placeholder="Pick project" />
    )}
    sx={{ minWidth: 360 }}
  />

  <Autocomplete
    size="small"
    options={areaOptions}
    value={selectedArea}
    onChange={(_, v) => setSelectedArea(v || null)}
    renderInput={(params) => (
      <TextField {...params} label="Area / Process" placeholder="Pick area/process" />
    )}
    sx={{ minWidth: 320 }}
  />

  <Button variant="outlined" startIcon={<AddOutlined />} onClick={onAddRow}>
    Add Row
  </Button>
</Box>


        <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
          Don’t see your <strong>Area / Process</strong>? Create a task for the selected
          project with that area/process on the{" "}
          <Link href="/tasks" underline="hover">Tasks</Link> page, then come back here.
        </Alert>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ position: "sticky", left: 0, zIndex: 2, bgcolor: "background.paper", minWidth: 260 }}>
                  Project / Area
                </TableCell>
                {dayKeys.map((dk) => {
                  const d = fromKey(dk);
                  const weekday = DOW[(d.getDay() + 6) % 7]; // Monday-first
                  const label = `${weekday} ${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;
                  return <TableCell key={dk} align="center">{label}</TableCell>;
                })}
                <TableCell align="right">Row Total</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={dayKeys.length + 3} align="center">
                    No rows. Add a project/area above.
                  </TableCell>
                </TableRow>
              )}

              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell
                    sx={{
                      position: "sticky", left: 0, zIndex: 1, bgcolor: "background.paper",
                      minWidth: 260, fontWeight: 500
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <span>{r.pName}</span>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.5 }}>
                        {r.area ? <Chip size="small" color="info" label={r.area} /> : null}
                      </Box>
                    </Box>
                  </TableCell>

                  {dayKeys.map((dk) => (
                    <TableCell key={dk} align="center" sx={{ minWidth: 90 }}>
                      <TextField
                        variant="standard"
                        value={cells[`${r.id}:${dk}`] ?? ""}
                        onChange={(e) => onChangeCell(r.id, dk, e.target.value)}
                        onBlur={(e) => {
                          const mins = hmToMinutes(e.target.value);
                          onChangeCell(r.id, dk, mins === null ? "" : minutesToHM(mins));
                        }}
                        placeholder="hh:mm"
                        inputProps={{
                          inputMode: "numeric",
                          pattern: "[0-9:.-]*",
                          style: { textAlign: "center", width: 54 }
                        }}
                      />
                    </TableCell>
                  ))}

                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {minutesToHM(rowTotalMins(r.id))}
                  </TableCell>

                  <TableCell align="center">
                    <Tooltip title="Remove row">
                      <IconButton onClick={() => onDeleteRow(r.id)}><DeleteOutline /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {rows.length > 0 && (
                <TableRow>
                  <TableCell sx={{ position: "sticky", left: 0, bgcolor: "background.paper", fontWeight: 700 }}>
                    Totals
                  </TableCell>
                  {dayKeys.map((dk) => (
                    <TableCell key={dk} align="center" sx={{ fontWeight: 700 }}>
                      {minutesToHM(dayTotalMins(dk))}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 800 }}>{minutesToHM(grandTotalMins())}</TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {/* success / error toast */}
      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
